import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosInstance from '@/utils/api/axios';
import { FilterRequest, AggregatePaginateResult } from '@/types';
import { HOST_API } from '../config-global';
import * as Sentry from '@sentry/react';

// Types for common API responses
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success?: boolean;
}

/**
 * Centralized API Service
 * Consolidates all direct axios calls from hooks into reusable methods
 */
export class ApiService {
  protected readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({ baseURL: HOST_API });
    this.initializeRequestInterceptor();
    this.initializeResponseInterceptor();
  }

  private initializeRequestInterceptor = () => {
    this.instance.interceptors.request.use(this.handleRequest, this.handleError);
  };

  private initializeResponseInterceptor = () => {
    this.instance.interceptors.response.use(this.handleResponse, this.handleError);
  };

  private handleRequest = (config: InternalAxiosRequestConfig) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  };

  private handleResponse = (response: AxiosResponse) => response;

  protected handleError = (error: AxiosError) =>
    // You can add custom error handling here if needed
    Promise.reject(error);

  public async get<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.instance.get<T>(url, config);
    return response.data;
  }

  public async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.instance.post<T>(url, data, config);
    return response.data;
  }

  public async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.instance.patch<T>(url, data, config);
    return response.data;
  }

  public async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.instance.put<T>(url, data, config);
    return response.data;
  }

  public async delete<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.instance.delete<T>(url, config);
    return response.data;
  }

  // ================================
  // REFERENCE DATA (GET /all endpoints)
  // ================================

  static async getIndustries(): Promise<ApiResponse> {
    const response = await axiosInstance.get('industries/all');
    return response.data;
  }

  static async getStaffUsers(): Promise<ApiResponse> {
    const response = await axiosInstance.get('users/staff');
    return response.data;
  }

  static async getDocumentTypes(): Promise<ApiResponse> {
    const response = await axiosInstance.get('document-types/all');
    return response.data;
  }

  static async getTermConditions(): Promise<ApiResponse> {
    const response = await axiosInstance.get('term-conditions/all');
    return response.data;
  }

  static async getPartners(): Promise<ApiResponse> {
    const response = await axiosInstance.get('partners/all');
    return response.data;
  }

  static async getBusinessUnits(): Promise<ApiResponse> {
    const response = await axiosInstance.get('business-units/all');
    return response.data;
  }

  // ================================
  // SUBSCRIPTIONS
  // ================================

  static async getFilteredSubscriptions(
    filters: FilterRequest
  ): Promise<AggregatePaginateResult<any>> {
    const response = await axiosInstance.post('/subscriptions/filtered', filters);
    return response.data;
  }

  static async deleteSubscription(id: string): Promise<void> {
    await axiosInstance.delete(`subscriptions/${id}`);
  }

  // ================================
  // ORDERS
  // ================================

  static async getFilteredOrders(filters: FilterRequest): Promise<AggregatePaginateResult<any>> {
    const response = await axiosInstance.post('/orders/filtered', filters);
    return response.data;
  }

  static async deleteOrder(id: string): Promise<void> {
    await axiosInstance.delete(`orders/${id}`);
  }

  // ================================
  // REQUESTS
  // ================================

  static async getFilteredRequests(filters: FilterRequest): Promise<AggregatePaginateResult<any>> {
    const response = await axiosInstance.post('/requests/filtered', filters);
    return response.data;
  }

  static async deleteRequest(id: string): Promise<void> {
    await axiosInstance.delete(`requests/${id}`);
  }

  static async approveRequest(id: string): Promise<void> {
    await axiosInstance.patch(`requests/${id}/approve`);
  }

  // ================================
  // ORGANIZATION DOCUMENTS
  // ================================

  static async getFilteredOrganizationDocuments(
    filters: FilterRequest
  ): Promise<AggregatePaginateResult<any>> {
    const response = await axiosInstance.post('/organization-documents/filtered', filters);
    return response.data;
  }

  static async deleteOrganizationDocument(id: string): Promise<void> {
    await axiosInstance.delete(`organization-documents/${id}`);
  }

  // ================================
  // LEGAL ENTITIES
  // ================================

  static async getLegalEntities(
    organizationId: string,
    params: Record<string, string>
  ): Promise<AggregatePaginateResult<any>> {
    const searchParams = new URLSearchParams(params);
    const response = await axiosInstance.get(
      `/legal-entities?organization=${organizationId}&${searchParams.toString()}`
    );
    return response.data;
  }

  static async deleteLegalEntity(id: string): Promise<void> {
    await axiosInstance.delete(`legal-entities/${id}`);
  }

  // ================================
  // REQUEST USERS
  // ================================

  static async getFilteredRequestUsers(
    filters: FilterRequest
  ): Promise<AggregatePaginateResult<any>> {
    const response = await axiosInstance.post('/users/approval/filtered', filters);
    return response.data;
  }

  // ================================
  // GENERIC FETCH METHOD
  // ================================

  static async fetchData<T = any>(
    endpoint: string,
    method: 'GET' | 'POST' = 'GET',
    data?: any,
    config?: any
  ): Promise<T> {
    const response =
      method === 'GET'
        ? await axiosInstance.get(endpoint, config)
        : await axiosInstance.post(endpoint, data, config);
    return response.data;
  }

  // ================================
  // BATCH OPERATIONS
  // ================================

  /**
   * Get multiple reference data types in parallel
   * Useful for loading filter options
   */
  static async getBatchReferenceData(types: string[]): Promise<Record<string, ApiResponse>> {
    const methodMap: Record<string, () => Promise<ApiResponse>> = {
      industries: this.getIndustries,
      staffUsers: this.getStaffUsers,
      documentTypes: this.getDocumentTypes,
      termConditions: this.getTermConditions,
      partners: this.getPartners,
      businessUnits: this.getBusinessUnits,
    };

    const promises = types.map(async (type) => {
      if (methodMap[type]) {
        try {
          const data = await methodMap[type]();
          return [type, data];
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              component: 'ApiService',
              action: 'getMultipleResourceTypes',
              resourceType: type,
            },
          });
          return [type, { data: [] }];
        }
      }
      return [type, { data: [] }];
    });

    const results = await Promise.all(promises);
    return Object.fromEntries(results);
  }
}

// Named exports for convenience
export const {
  getIndustries,
  getStaffUsers,
  getDocumentTypes,
  getTermConditions,
  getPartners,
  getBusinessUnits,
  getFilteredSubscriptions,
  deleteSubscription,
  getFilteredOrders,
  deleteOrder,
  getFilteredRequests,
  deleteRequest,
  approveRequest,
  getFilteredOrganizationDocuments,
  deleteOrganizationDocument,
  getLegalEntities,
  deleteLegalEntity,
  getFilteredRequestUsers,
  fetchData,
  getBatchReferenceData,
} = ApiService;
